// client/src/components/pages/OrderManagement/Vehicles/index.tsx (converted from .js, ST-00216)

import {
  useState,
  useEffect,
  useCallback,
  type ChangeEvent,
  type ComponentType,
  type FormEvent,
  type MouseEvent,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Truck,
  Plus,
  Search,
  X,
  Check,
  Loader,
  Edit2,
  Trash2,
  ArrowLeft,
  Shield,
  ShieldOff,
  User,
  Filter,
  UserPlus,
  XCircle,
} from 'lucide-react';
import orderService from '../../../../services/orderService';
import { TableSkeleton, EmptyState, haulierChoices } from '../shared';
import { apiErrorReason, ORDERS_MANAGE } from '../shared/orderHelpers';
import { useAuth } from '../../../../types/commonComponents';
import ThemedSelectJs from '../../../common/ThemedSelect';
import { ThemedSelect, type ThemedSelectProps } from '../../../../types/commonComponents';
import type {
  ApiErrorLike,
  CompanyId,
  DriverRow,
  FleetStats,
  OrderCompany,
  VehicleRow,
} from '../../../../types/clientPages/orderManagement';

/**
 * ThemedSelect over company ids: it matches the value with String() and emits the chosen
 * option's own value, which is the numeric company_id (or '' for the empty option).
 */
const CompanySelect = ThemedSelectJs as unknown as ComponentType<
  Omit<ThemedSelectProps, 'value' | 'onChange'> & {
    value: CompanyId | '';
    onChange: (e: { target: { name?: string; value: CompanyId | '' } }) => void;
  }
>;

type StatKey = keyof FleetStats;

const VEHICLE_TYPES = [
  { value: 'rigid', label: 'Rigid' },
  { value: 'horse_trailer', label: 'Horse & Trailer' },
  { value: 'interlink', label: 'Interlink' },
  { value: 'side_tipper', label: 'Side Tipper' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'emerald' },
  { value: 'suspended', label: 'Suspended', color: 'amber' },
  { value: 'blacklisted', label: 'Blacklisted', color: 'red' },
];

const VEHICLE_TYPE_STYLES: Record<string, string | undefined> & { other: string } = {
  rigid: 'bg-blue-50 text-blue-700',
  horse_trailer: 'bg-purple-50 text-purple-700',
  interlink: 'bg-teal-50 text-teal-700',
  side_tipper: 'bg-amber-50 text-amber-700',
  other: 'bg-slate-100 text-slate-600',
};

const STATUS_STYLES: Record<string, string | undefined> = {
  active: 'bg-emerald-50 text-emerald-700',
  suspended: 'bg-amber-50 text-amber-700',
  blacklisted: 'bg-red-50 text-red-700',
};

const STAT_BORDERS: Record<StatKey, string> = {
  total: 'border-l-slate-400',
  whitelisted: 'border-l-teal-500',
  active: 'border-l-emerald-500',
  suspended: 'border-l-amber-500',
  blacklisted: 'border-l-red-500',
};

const STAT_COLORS: Record<StatKey, string> = {
  total: 'text-slate-700',
  whitelisted: 'text-teal-700',
  active: 'text-emerald-700',
  suspended: 'text-amber-700',
  blacklisted: 'text-red-700',
};

const mono = { fontFamily: 'IBM Plex Mono, monospace' };
const body = { fontFamily: 'DM Sans, sans-serif' };

function getTypeLabel(value: string) {
  const t = VEHICLE_TYPES.find((v) => v.value === value);
  return t ? t.label : value || 'Unknown';
}

// Prefer the server's message ("A vehicle with this plate number already exists")
// over axios' generic "Request failed with status code 409"; the permission gate's 403
// reads "Your role does not allow managing orders…" (ST-00227 / ORD-12).
const errorText = apiErrorReason;

function formatWeight(val: number | string | null | undefined) {
  if (val == null || val === '') return '-';
  return Number(val).toLocaleString() + ' kg';
}

interface VehicleForm {
  plate_number: string;
  vehicle_type: string;
  hauler_company_id: CompanyId | '';
  stored_tare_kg: number | string;
  max_gvw_kg: number | string;
  whitelisted: boolean;
  status: string;
  notes: string;
}

const Vehicles = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  // Writes need orders.manage on the server (ST-00227 / ORD-12); without it the page is read-only.
  const canManage = hasPermission(ORDERS_MANAGE);

  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [companies, setCompanies] = useState<OrderCompany[]>([]);
  const [stats, setStats] = useState<FleetStats>({
    total: 0,
    whitelisted: 0,
    active: 0,
    suspended: 0,
    blacklisted: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [whitelistFilter, setWhitelistFilter] = useState('all');
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleRow | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<VehicleRow | null>(null);
  const [formData, setFormData] = useState<VehicleForm>({
    plate_number: '',
    vehicle_type: 'rigid',
    hauler_company_id: '',
    stored_tare_kg: '',
    max_gvw_kg: '',
    whitelisted: false,
    status: 'active',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [togglingWhitelist, setTogglingWhitelist] = useState<string | null>(null);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [allDrivers, setAllDrivers] = useState<DriverRow[]>([]);
  const [addingDriver, setAddingDriver] = useState(false);
  const [driverToAdd, setDriverToAdd] = useState('');
  const [driverPrimary, setDriverPrimary] = useState(false);
  const [removingDriverId, setRemovingDriverId] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [vehiclesRes, companiesRes, statsRes, driversRes] = await Promise.all([
        orderService.getVehicles(),
        orderService.getCompanies(),
        orderService.getVehicleStats(),
        orderService.getDrivers(),
      ]);
      setVehicles(vehiclesRes.vehicles || []);
      setCompanies(companiesRes.companies || []);
      setStats(
        statsRes.stats || { total: 0, whitelisted: 0, active: 0, suspended: 0, blacklisted: 0 }
      );
      setAllDrivers(driversRes.drivers || []);
    } catch (err) {
      setError((err as ApiErrorLike).message || 'Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredVehicles = vehicles.filter((v) => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const plate = (v.plate_number || '').toLowerCase();
      const hauler = (v.hauler_name || '').toLowerCase();
      if (!plate.includes(term) && !hauler.includes(term)) return false;
    }
    if (statusFilter !== 'all' && v.status !== statusFilter) return false;
    if (whitelistFilter === 'whitelisted' && !v.whitelisted) return false;
    if (whitelistFilter === 'not_whitelisted' && v.whitelisted) return false;
    return true;
  });

  const handleSelectRow = async (vehicle: VehicleRow) => {
    if (selectedVehicle?.vehicle_id === vehicle.vehicle_id) {
      setSelectedVehicle(null);
    } else {
      setSelectedVehicle(vehicle);
      // Fetch full detail with drivers
      try {
        const res = await orderService.getVehicle(vehicle.vehicle_id);
        if (res.vehicle) setSelectedVehicle(res.vehicle);
      } catch (err) {
        // Keep the row data if detail fetch fails
        console.error('Failed to fetch vehicle detail:', err);
      }
    }
  };

  const handleToggleSelect = (id: string, e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredVehicles.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredVehicles.map((v) => v.vehicle_id));
    }
  };

  const handleWhitelistToggle = async (vehicle: VehicleRow, e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const newVal = !vehicle.whitelisted;
    setTogglingWhitelist(vehicle.vehicle_id);
    try {
      await orderService.updateVehicle(vehicle.vehicle_id, { whitelisted: newVal });
      setVehicles((prev) =>
        prev.map((v) => (v.vehicle_id === vehicle.vehicle_id ? { ...v, whitelisted: newVal } : v))
      );
      // The panel may have been closed or moved to another vehicle while the call ran:
      // only update it when it still shows this vehicle (port note #11).
      setSelectedVehicle((prev) =>
        prev && prev.vehicle_id === vehicle.vehicle_id ? { ...prev, whitelisted: newVal } : prev
      );
      setStats((prev) => ({
        ...prev,
        whitelisted: newVal ? prev.whitelisted + 1 : prev.whitelisted - 1,
      }));
    } catch (err) {
      alert('Failed to update whitelist status: ' + errorText(err));
    } finally {
      setTogglingWhitelist(null);
    }
  };

  const handleBulkWhitelist = async (value: boolean) => {
    if (selectedIds.length === 0) return;
    const action = value ? 'whitelist' : 'remove from whitelist';
    if (!window.confirm(`Are you sure you want to ${action} ${selectedIds.length} vehicle(s)?`))
      return;
    setBulkProcessing(true);
    try {
      await orderService.bulkWhitelistVehicles(selectedIds, value);
      await loadData();
      setSelectedIds([]);
    } catch (err) {
      alert('Bulk operation failed: ' + errorText(err));
    } finally {
      setBulkProcessing(false);
    }
  };

  const openCreateModal = () => {
    setEditingVehicle(null);
    setFormData({
      plate_number: '',
      vehicle_type: 'rigid',
      hauler_company_id: '',
      stored_tare_kg: '',
      max_gvw_kg: '',
      whitelisted: false,
      status: 'active',
      notes: '',
    });
    setModalOpen(true);
  };

  const openEditModal = (vehicle: VehicleRow, e?: MouseEvent<HTMLButtonElement>) => {
    if (e) e.stopPropagation();
    setEditingVehicle(vehicle);
    setFormData({
      plate_number: vehicle.plate_number || '',
      vehicle_type: vehicle.vehicle_type || 'rigid',
      hauler_company_id: vehicle.hauler_company_id || '',
      stored_tare_kg: vehicle.stored_tare_kg ?? '',
      max_gvw_kg: vehicle.max_gvw_kg ?? '',
      whitelisted: !!vehicle.whitelisted,
      status: vehicle.status || 'active',
      notes: vehicle.notes || '',
    });
    setModalOpen(true);
  };

  const handleDelete = async (vehicle: VehicleRow, e?: MouseEvent<HTMLButtonElement>) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`Delete vehicle ${vehicle.plate_number}? This action cannot be undone.`))
      return;
    try {
      await orderService.deleteVehicle(vehicle.vehicle_id);
      if (selectedVehicle?.vehicle_id === vehicle.vehicle_id) {
        setSelectedVehicle(null);
      }
      await loadData();
    } catch (err) {
      alert('Failed to delete vehicle: ' + errorText(err));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const plate = formData.plate_number.trim().toUpperCase();
    if (!plate) {
      alert('Plate number is required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        plate_number: plate,
        stored_tare_kg: formData.stored_tare_kg !== '' ? Number(formData.stored_tare_kg) : null,
        max_gvw_kg: formData.max_gvw_kg !== '' ? Number(formData.max_gvw_kg) : null,
        hauler_company_id: formData.hauler_company_id || null,
      };
      if (editingVehicle) {
        await orderService.updateVehicle(editingVehicle.vehicle_id, payload);
      } else {
        await orderService.createVehicle(payload);
      }
      setModalOpen(false);
      setEditingVehicle(null);
      await loadData();
      // Keep the detail panel in step with what was just saved.
      if (editingVehicle && selectedVehicle?.vehicle_id === editingVehicle.vehicle_id) {
        await refreshSelectedVehicle(editingVehicle.vehicle_id);
      }
    } catch (err) {
      alert('Failed to save vehicle: ' + errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = <K extends keyof VehicleForm>(field: K, value: VehicleForm[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const refreshSelectedVehicle = async (vehicleId: string) => {
    try {
      const res = await orderService.getVehicle(vehicleId);
      if (res.vehicle) setSelectedVehicle(res.vehicle);
    } catch (err) {
      console.error('Failed to refresh vehicle detail:', err);
    }
  };

  const handleAddDriver = async () => {
    if (!driverToAdd || !selectedVehicle) return;
    setAddingDriver(true);
    try {
      await orderService.addDriverToVehicle(selectedVehicle.vehicle_id, driverToAdd, driverPrimary);
      await refreshSelectedVehicle(selectedVehicle.vehicle_id);
      setDriverToAdd('');
      setDriverPrimary(false);
    } catch (err) {
      alert('Failed to add driver: ' + errorText(err));
    } finally {
      setAddingDriver(false);
    }
  };

  const handleRemoveDriver = async (driverId: string) => {
    if (!selectedVehicle) return;
    setRemovingDriverId(driverId);
    try {
      await orderService.removeDriverFromVehicle(selectedVehicle.vehicle_id, driverId);
      await refreshSelectedVehicle(selectedVehicle.vehicle_id);
    } catch (err) {
      alert('Failed to remove driver: ' + errorText(err));
    } finally {
      setRemovingDriverId(null);
    }
  };

  if (loading) {
    return (
      <div style={body} className="min-h-screen bg-slate-50 p-6">
        <TableSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div style={body} className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-2xl mx-auto mt-20 text-center">
          <div className="bg-surface rounded-xl border border-red-200 p-8">
            <p className="text-red-600 font-medium mb-4">{error}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={body} className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Back navigation */}
        <button
          onClick={() => navigate('/settings/modules/order-management/master-data')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-4 group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
          <span>Master Data</span>
        </button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 bg-primary-600 rounded-lg flex items-center justify-center">
              <Truck size={22} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-slate-900">Vehicles</h1>
                <span
                  className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full"
                  style={mono}
                >
                  {stats.total}
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">Fleet registry and whitelisting</p>
            </div>
          </div>
          {canManage && (
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors text-sm font-medium shadow-sm"
            >
              <Plus size={16} />
              Add Vehicle
            </button>
          )}
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {(
            [
              { key: 'total', label: 'Total' },
              { key: 'whitelisted', label: 'Whitelisted' },
              { key: 'active', label: 'Active' },
              { key: 'suspended', label: 'Suspended' },
              { key: 'blacklisted', label: 'Blacklisted' },
            ] as const
          ).map(({ key, label }) => (
            <div
              key={key}
              className={`bg-surface rounded-lg border border-slate-200 border-l-[3px] ${STAT_BORDERS[key]} px-4 py-3`}
            >
              <p className="text-xs text-slate-500 mb-1">{label}</p>
              <p className={`text-xl font-bold ${STAT_COLORS[key]}`} style={mono}>
                {stats[key] ?? 0}
              </p>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="bg-surface rounded-lg border border-slate-200 px-4 py-3 mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[180px] max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search plate or haulier..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="hidden sm:block h-6 w-px bg-slate-200" />

          <div className="flex items-center gap-1.5">
            {[
              { value: 'all', label: 'All' },
              { value: 'active', label: 'Active' },
              { value: 'suspended', label: 'Suspended' },
              { value: 'blacklisted', label: 'Blacklisted' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  statusFilter === opt.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="hidden sm:block h-6 w-px bg-slate-200" />

          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-400" />
            <ThemedSelect
              value={whitelistFilter}
              onChange={(e) => setWhitelistFilter(e.target.value)}
              className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-surface"
            >
              <option value="all">All</option>
              <option value="whitelisted">Whitelisted</option>
              <option value="not_whitelisted">Not Whitelisted</option>
            </ThemedSelect>
          </div>

          {canManage && selectedIds.length > 0 && (
            <>
              <div className="hidden sm:block h-6 w-px bg-slate-200" />
              <span className="text-xs text-slate-500">{selectedIds.length} selected</span>
              <button
                onClick={() => handleBulkWhitelist(true)}
                disabled={bulkProcessing}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 rounded-lg text-xs font-medium hover:bg-teal-100 transition-colors disabled:opacity-50"
              >
                {bulkProcessing ? (
                  <Loader size={13} className="animate-spin" />
                ) : (
                  <Shield size={13} />
                )}
                Whitelist
              </button>
              <button
                onClick={() => handleBulkWhitelist(false)}
                disabled={bulkProcessing}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-200 transition-colors disabled:opacity-50"
              >
                {bulkProcessing ? (
                  <Loader size={13} className="animate-spin" />
                ) : (
                  <ShieldOff size={13} />
                )}
                Remove
              </button>
            </>
          )}
        </div>

        {/* Master-detail layout */}
        {filteredVehicles.length === 0 ? (
          <EmptyState
            icon={Truck}
            title="No vehicles found"
            description={
              searchTerm || statusFilter !== 'all' || whitelistFilter !== 'all'
                ? 'Try adjusting your search or filters.'
                : 'Add your first vehicle to get started.'
            }
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Table */}
            <div
              className={`${selectedVehicle ? 'lg:col-span-2' : 'lg:col-span-3'} transition-all`}
            >
              <div className="bg-surface rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50/60">
                        {canManage && (
                          <th className="pl-4 pr-2 py-3 text-left w-10">
                            <input
                              type="checkbox"
                              aria-label="Select all vehicles"
                              checked={
                                selectedIds.length === filteredVehicles.length &&
                                filteredVehicles.length > 0
                              }
                              onChange={handleSelectAll}
                              className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                            />
                          </th>
                        )}
                        <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Plate
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Haulier
                        </th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Tare
                        </th>
                        <th className="px-3 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Max GVW
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Whitelist
                        </th>
                        {canManage && (
                          <th className="px-3 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredVehicles.map((vehicle) => {
                        const isSelected = selectedVehicle?.vehicle_id === vehicle.vehicle_id;
                        return (
                          <tr
                            key={vehicle.vehicle_id}
                            onClick={() => handleSelectRow(vehicle)}
                            className={`cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-teal-50/60 border-l-2 border-l-teal-500'
                                : 'hover:bg-slate-50/80'
                            }`}
                          >
                            {canManage && (
                              <td className="pl-4 pr-2 py-3">
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${vehicle.plate_number}`}
                                  checked={selectedIds.includes(vehicle.vehicle_id)}
                                  onChange={(e) => handleToggleSelect(vehicle.vehicle_id, e)}
                                  className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                                />
                              </td>
                            )}
                            <td className="px-3 py-3">
                              <span className="font-bold text-slate-900" style={mono}>
                                {vehicle.plate_number}
                              </span>
                            </td>
                            <td className="px-3 py-3">
                              <span
                                className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${VEHICLE_TYPE_STYLES[vehicle.vehicle_type] || VEHICLE_TYPE_STYLES.other}`}
                              >
                                {getTypeLabel(vehicle.vehicle_type)}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-slate-600">
                              {vehicle.hauler_name || '-'}
                            </td>
                            <td className="px-3 py-3 text-right text-slate-600" style={mono}>
                              {formatWeight(vehicle.stored_tare_kg)}
                            </td>
                            <td className="px-3 py-3 text-right text-slate-600" style={mono}>
                              {formatWeight(vehicle.max_gvw_kg)}
                            </td>
                            <td className="px-3 py-3 text-center">
                              <span
                                className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[vehicle.status] || 'bg-slate-100 text-slate-600'}`}
                              >
                                {vehicle.status
                                  ? vehicle.status.charAt(0).toUpperCase() + vehicle.status.slice(1)
                                  : '-'}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-center">
                              {canManage ? (
                                <button
                                  onClick={(e) => handleWhitelistToggle(vehicle, e)}
                                  disabled={togglingWhitelist === vehicle.vehicle_id}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                    vehicle.whitelisted
                                      ? 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                                      : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                  }`}
                                >
                                  {togglingWhitelist === vehicle.vehicle_id ? (
                                    <Loader size={12} className="animate-spin" />
                                  ) : vehicle.whitelisted ? (
                                    <Check size={12} />
                                  ) : (
                                    <X size={12} />
                                  )}
                                  {vehicle.whitelisted ? 'Yes' : 'No'}
                                </button>
                              ) : (
                                <span
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                                    vehicle.whitelisted
                                      ? 'bg-teal-100 text-teal-700'
                                      : 'bg-slate-100 text-slate-500'
                                  }`}
                                >
                                  {vehicle.whitelisted ? <Check size={12} /> : <X size={12} />}
                                  {vehicle.whitelisted ? 'Yes' : 'No'}
                                </span>
                              )}
                            </td>
                            {canManage && (
                              <td className="px-3 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={(e) => openEditModal(vehicle, e)}
                                    className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                                    title="Edit"
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                  <button
                                    onClick={(e) => handleDelete(vehicle, e)}
                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                    title="Delete"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Detail panel */}
            {selectedVehicle && (
              <div className="lg:col-span-1">
                <div className="sticky top-6 bg-surface rounded-lg border border-slate-200 overflow-hidden">
                  <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-bold text-slate-900" style={mono}>
                        {selectedVehicle.plate_number}
                      </span>
                      {selectedVehicle.whitelisted ? (
                        <Shield size={18} className="text-teal-500" />
                      ) : (
                        <ShieldOff size={18} className="text-slate-300" />
                      )}
                    </div>
                    <button
                      onClick={() => setSelectedVehicle(null)}
                      className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
                      aria-label="Close details"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="px-5 py-4 space-y-4">
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Type</p>
                      <span
                        className={`inline-block px-2.5 py-1 rounded text-xs font-medium ${VEHICLE_TYPE_STYLES[selectedVehicle.vehicle_type] || VEHICLE_TYPE_STYLES.other}`}
                      >
                        {getTypeLabel(selectedVehicle.vehicle_type)}
                      </span>
                    </div>

                    <div>
                      <p className="text-xs text-slate-500 mb-1">Haulier</p>
                      <p className="text-sm text-slate-800 font-medium">
                        {selectedVehicle.hauler_name || '-'}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-slate-500 mb-1">Status</p>
                      <span
                        className={`inline-block px-2.5 py-1 rounded text-xs font-medium ${STATUS_STYLES[selectedVehicle.status] || 'bg-slate-100 text-slate-600'}`}
                      >
                        {selectedVehicle.status
                          ? selectedVehicle.status.charAt(0).toUpperCase() +
                            selectedVehicle.status.slice(1)
                          : '-'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Tare</p>
                        <p className="text-sm text-slate-800 font-medium" style={mono}>
                          {formatWeight(selectedVehicle.stored_tare_kg)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Max GVW</p>
                        <p className="text-sm text-slate-800 font-medium" style={mono}>
                          {formatWeight(selectedVehicle.max_gvw_kg)}
                        </p>
                      </div>
                    </div>

                    {selectedVehicle.notes && (
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Notes</p>
                        <p className="text-sm text-slate-600 leading-relaxed">
                          {selectedVehicle.notes}
                        </p>
                      </div>
                    )}

                    <div>
                      <p className="text-xs text-slate-500 mb-2">
                        Assigned Drivers{' '}
                        {!!selectedVehicle.drivers &&
                          selectedVehicle.drivers.length > 0 &&
                          `(${selectedVehicle.drivers.length})`}
                      </p>
                      {selectedVehicle.drivers && selectedVehicle.drivers.length > 0 && (
                        <div className="space-y-2 mb-3">
                          {selectedVehicle.drivers.map((driver) => (
                            <div
                              key={driver.driver_id}
                              className="flex items-center gap-2.5 px-3 py-2 bg-slate-50 rounded-lg group"
                            >
                              <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center">
                                <User size={13} className="text-slate-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-slate-800 font-medium truncate">
                                  {driver.driver_name}
                                </p>
                              </div>
                              {driver.is_primary && (
                                <span className="px-1.5 py-0.5 bg-teal-50 text-teal-700 text-[10px] font-medium rounded">
                                  Primary
                                </span>
                              )}
                              {canManage && (
                                <button
                                  onClick={() => handleRemoveDriver(driver.driver_id)}
                                  disabled={removingDriverId === driver.driver_id}
                                  className="p-1 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors opacity-0 group-hover:opacity-100"
                                  title="Remove driver"
                                >
                                  {removingDriverId === driver.driver_id ? (
                                    <Loader size={12} className="animate-spin" />
                                  ) : (
                                    <XCircle size={14} />
                                  )}
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Add driver form */}
                      {canManage &&
                        (() => {
                          const assignedIds = new Set(
                            (selectedVehicle.drivers || []).map((d) => d.driver_id)
                          );
                          const availableDrivers = allDrivers.filter(
                            (d) => !assignedIds.has(d.driver_id) && d.status === 'active'
                          );
                          if (
                            availableDrivers.length === 0 &&
                            (!selectedVehicle.drivers || selectedVehicle.drivers.length === 0)
                          ) {
                            return (
                              <p className="text-xs text-slate-400">
                                No drivers available. Add drivers first.
                              </p>
                            );
                          }
                          if (availableDrivers.length === 0) return null;
                          return (
                            <div className="flex items-center gap-2">
                              <ThemedSelect
                                value={driverToAdd}
                                onChange={(e) => setDriverToAdd(e.target.value)}
                                className="flex-1 text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-surface"
                              >
                                <option value="">Add driver...</option>
                                {availableDrivers.map((d) => (
                                  <option key={d.driver_id} value={d.driver_id}>
                                    {d.driver_name}
                                  </option>
                                ))}
                              </ThemedSelect>
                              <label className="flex items-center gap-1 text-[10px] text-slate-500 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={driverPrimary}
                                  onChange={(e) => setDriverPrimary(e.target.checked)}
                                  className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 w-3 h-3"
                                />
                                Primary
                              </label>
                              <button
                                onClick={handleAddDriver}
                                disabled={!driverToAdd || addingDriver}
                                className="p-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors disabled:opacity-40"
                                title="Add driver"
                              >
                                {addingDriver ? (
                                  <Loader size={13} className="animate-spin" />
                                ) : (
                                  <UserPlus size={13} />
                                )}
                              </button>
                            </div>
                          );
                        })()}
                    </div>
                  </div>

                  {canManage && (
                    <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-2">
                      <button
                        onClick={(e) => openEditModal(selectedVehicle, e)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        <Edit2 size={13} />
                        Edit
                      </button>
                      <button
                        onClick={(e) => handleDelete(selectedVehicle, e)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg text-sm font-medium transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !saving && setModalOpen(false)}
          />
          <div
            className="relative bg-surface rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto"
            style={body}
          >
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                {editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}
              </h2>
              <button
                onClick={() => !saving && setModalOpen(false)}
                className="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Plate Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.plate_number}
                  onChange={(e) => handleFormChange('plate_number', e.target.value.toUpperCase())}
                  placeholder="e.g. ABC 123 GP"
                  required
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-colors"
                  style={mono}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Vehicle Type
                </label>
                <ThemedSelect
                  value={formData.vehicle_type}
                  onChange={(e) => handleFormChange('vehicle_type', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-surface"
                >
                  {VEHICLE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Haulier</label>
                <CompanySelect
                  value={formData.hauler_company_id}
                  onChange={(e) => handleFormChange('hauler_company_id', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-surface"
                >
                  <option value="">Select haulier...</option>
                  {haulierChoices(
                    companies,
                    formData.hauler_company_id,
                    editingVehicle?.hauler_name
                  ).map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </CompanySelect>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Stored Tare (kg)
                  </label>
                  <input
                    type="number"
                    value={formData.stored_tare_kg}
                    onChange={(e) => handleFormChange('stored_tare_kg', e.target.value)}
                    placeholder="e.g. 8500"
                    min="0"
                    step="0.1"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    style={mono}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Max GVW (kg)
                  </label>
                  <input
                    type="number"
                    value={formData.max_gvw_kg}
                    onChange={(e) => handleFormChange('max_gvw_kg', e.target.value)}
                    placeholder="e.g. 56000"
                    min="0"
                    step="0.1"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                    style={mono}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <ThemedSelect
                  value={formData.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 bg-surface"
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </ThemedSelect>
              </div>

              <div className="flex items-center justify-between py-2">
                <label className="text-sm font-medium text-slate-700">Whitelisted</label>
                <button
                  type="button"
                  onClick={() => handleFormChange('whitelisted', !formData.whitelisted)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.whitelisted ? 'bg-teal-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-surface transition-transform shadow-sm ${
                      formData.whitelisted ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleFormChange('notes', e.target.value)}
                  rows={3}
                  placeholder="Optional notes..."
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 resize-none"
                />
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 shadow-sm"
                >
                  {saving && <Loader size={14} className="animate-spin" />}
                  {editingVehicle ? 'Update Vehicle' : 'Create Vehicle'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vehicles;
